// # WebID sharing component for profile pages
import React, { useState, useEffect } from 'react';
import { MdClose, MdContentCopy, MdQrCode, MdLink } from 'react-icons/md';
import { FaTwitter, FaFacebook, FaWhatsapp } from 'react-icons/fa';
import { QRCodeSVG } from 'qrcode.react'; // # Import QRCodeSVG component
import { useAuthStore } from '../../auth/state'; // # Import auth store
import './web-id.css';

const WebIdShare = ({ username, isOwner, onClose }) => {
  // # State for link copied notification
  const [linkCopied, setLinkCopied] = useState(false);
  // # State for selected sharing tab
  const [activeTab, setActiveTab] = useState('link');
  // # State for full profile URL
  const [profileUrl, setProfileUrl] = useState('');
  // # Get current user profile to check ownership
  const { userProfile } = useAuthStore();
  // # State to track if this is the user's own profile
  const [isOwnProfile, setIsOwnProfile] = useState(isOwner !== undefined ? isOwner : false);
  
  // # Check if this is the user's own profile if isOwner prop is not provided
  useEffect(() => {
    if (isOwner !== undefined) {
      setIsOwnProfile(isOwner);
    } else if (userProfile && username) {
      setIsOwnProfile(userProfile.username === username);
    }
  }, [userProfile, username, isOwner]);
  
  // # Get current domain and set full profile URL
  useEffect(() => {
    const domain = window.location.origin;
    setProfileUrl(`${domain}/profile/${username}`);
  }, [username]);
  
  // # Handle copy link function
  const handleCopyLink = () => {
    navigator.clipboard.writeText(profileUrl).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 3000);
    });
  };
  
  // # Generate social sharing text based on ownership
  const sharingText = isOwnProfile 
    ? `Check out my profile at TEMP-U` 
    : `Check out this profile at TEMP-U`;
  
  // # Generate social sharing links
  const twitterShareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(profileUrl)}&text=${encodeURIComponent(sharingText)}`;
  const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(profileUrl)}`;
  const whatsappShareUrl = `https://wa.me/?text=${encodeURIComponent(`${sharingText}: ${profileUrl}`)}`;
  
  return (
    <div className="webid_overlay" onClick={onClose}>
      <div className="webid_modal" onClick={(e) => e.stopPropagation()}>
        {/* # Modal header */}
        <div className="webid_header">
          <h2>Share Profile</h2>
          <button className="webid_close-btn" onClick={onClose}>
            <MdClose />
          </button>
        </div>
        
        {/* # Tabs */}
        <div className="webid_tabs">
          <button 
            className={`webid_tab ${activeTab === 'link' ? 'active' : ''}`}
            onClick={() => setActiveTab('link')}
          >
            <MdLink />
            <span>Link</span>
          </button>
          <button 
            className={`webid_tab ${activeTab === 'qr' ? 'active' : ''}`}
            onClick={() => setActiveTab('qr')}
          >
            <MdQrCode />
            <span>QR Code</span>
          </button>
          <button 
            className={`webid_tab ${activeTab === 'social' ? 'active' : ''}`}
            onClick={() => setActiveTab('social')}
          >
            <FaTwitter />
            <span>Social</span>
          </button>
        </div>
        
        {/* # Tab content */}
        <div className="webid_content">
          {/* # Link sharing */}
          {activeTab === 'link' && (
            <div className="webid_link-section">
              <p className="webid_instruction">{isOwnProfile ? 'Copy this link to share your profile:' : 'Copy this link to share the profile:'}</p>
              <div className="webid_link-container">
                <input 
                  type="text" 
                  value={profileUrl} 
                  readOnly 
                  className="webid_link-input"
                />
                <button 
                  className="webid_copy-btn"
                  onClick={handleCopyLink}
                  title="Copy to clipboard"
                >
                  <MdContentCopy />
                </button>
              </div>
              {linkCopied && (
                <div className="webid_copied-message">
                  Link copied to clipboard!
                </div>
              )}
            </div>
          )}
          
          {/* # QR Code */}
          {activeTab === 'qr' && (
            <div className="webid_qr-section">
              <p className="webid_instruction">{isOwnProfile ? 'Scan this QR Code to visit your profile:' : 'Scan this QR Code to visit the profile:'}</p>
              <div className="webid_qr-container">
                <QRCodeSVG 
                  value={profileUrl}
                  size={200}
                  level="H"
                  includeMargin={true}
                  className="webid_qr-code"
                />
              </div>
              <button 
                className="webid_download-btn"
                onClick={() => {
                  // # Create download link for QR code
                  const svg = document.querySelector('.webid_qr-code');
                  if (svg) {
                    const svgData = new XMLSerializer().serializeToString(svg);
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');
                    const img = new Image();
                    img.onload = () => {
                      canvas.width = 200;
                      canvas.height = 200;
                      ctx.drawImage(img, 0, 0);
                      const url = canvas.toDataURL('image/png');
                      const link = document.createElement('a');
                      link.download = `${username}-profile-qr.png`;
                      link.href = url;
                      link.click();
                    };
                    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
                  }
                }}
              >
                Download QR Code
              </button>
            </div>
          )}
          
          {/* # Social sharing */}
          {activeTab === 'social' && (
            <div className="webid_social-section">
              <p className="webid_instruction">{isOwnProfile ? 'Share your profile on social media:' : 'Share on social media:'}</p>
              <div className="webid_social-buttons">
                <a 
                  href={twitterShareUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="webid_social-btn twitter"
                >
                  <FaTwitter />
                  <span>Twitter</span>
                </a>
                <a 
                  href={facebookShareUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="webid_social-btn facebook"
                >
                  <FaFacebook />
                  <span>Facebook</span>
                </a>
                <a 
                  href={whatsappShareUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="webid_social-btn whatsapp"
                >
                  <FaWhatsapp />
                  <span>WhatsApp</span>
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WebIdShare;
